import { useMemo, useEffect, memo, useRef } from 'react'
import PropTypes from 'prop-types'
import { FormProvider, useForm } from 'react-hook-form'
import { useMediaQuery, useTheme, Box } from '@mui/material'
import { t } from 'i18next'

import { capitalizeFirstLetter } from '@hapana/shared/utils/string.util'
import TableFilterSelect from '../../../../../components/shared/table/components/filters/table-filter-select.component'

import {
  CLIENT_SIDE_SELECT_FIELD_NAME,
  FILTER_DEFAULT_VALUE,
  FILTER_MODES,
  FILTER_SELECT_TYPES,
} from '../../table.constants'
import { styles } from '../../table.styles'

import TableFilterMobileSelect from './table-filter-mobile-select.component'

const TableFilter = ({
  column: {
    preFilteredRows,
    id,
    render,
    filterValue,
    serverSideFilterOptions,
    hasCutomFilterHeader,
    filterStyles,
    filterContainerStyles,
    filterComponent,
    filterComponentStyles,
  },
  setFilter,
  filterMode,
  filterSelectType,
  shouldClearFilters,
  setShouldClearFilters,
  isFiltersLoading,
  muiSelectProps,
  disabled,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const theme = useTheme()
  const isInitialMount = useRef(true)

  const options = useMemo(() => {
    const newOptions = []
    if (filterMode === FILTER_MODES.CLIENT_SIDE) {
      preFilteredRows.forEach((row) => {
        if (newOptions.find((option) => option.value === row.values[id])) return
        newOptions.push({
          value: row.values[id],
          label: capitalizeFirstLetter(row.values[id]),
        })
      })
      return newOptions
    } else {
      return serverSideFilterOptions || []
    }
  }, [id, preFilteredRows, filterMode, serverSideFilterOptions])

  const fieldName = `${CLIENT_SIDE_SELECT_FIELD_NAME}-${id}`

  const formMethods = useForm({
    defaultValues: {
      [fieldName]: filterValue || FILTER_DEFAULT_VALUE,
    },
  })

  const { watch, setValue } = formMethods
  const filteredValue = watch(fieldName)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    setFilter(id, filteredValue || filteredValue === false ? filteredValue : undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredValue, id])

  useEffect(() => {
    if (shouldClearFilters) {
      setValue(fieldName, FILTER_DEFAULT_VALUE)
      setShouldClearFilters(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldClearFilters, fieldName])

  const selectType =
    filterSelectType === FILTER_SELECT_TYPES.CHECKBOX
      ? FILTER_SELECT_TYPES.select
      : filterSelectType

  const headerContent = hasCutomFilterHeader ? render('customFilterHeader') : render('Header')
  // Handle component filter type - render custom component directly
  if (filterSelectType === FILTER_SELECT_TYPES.component) {
    return (
      <Box sx={[styles.componentFilterContainer, filterComponentStyles]}>
        {filterComponent || null}
      </Box>
    )
  }

  const mobilePrefix = isMobile ? t('common:select') : ''
  const mobileHeaderContent = isMobile ? headerContent.toLowerCase() : headerContent

  return (
    <FormProvider {...formMethods}>
      <form
        style={isMobile ? styles.tableFormMobile : styles.tableForm({ palette: theme.palette })}
      >
        {isMobile ? (
          <TableFilterMobileSelect
            name={fieldName}
            placeholder={`${mobilePrefix}${' '}${mobileHeaderContent}`}
            options={options}
            type={selectType}
            filterStyles={filterStyles}
            isFiltersLoading={isFiltersLoading}
            muiSelectProps={muiSelectProps}
            filterContainerStyles={filterContainerStyles}
            initialValue={filterValue}
            disabled={disabled}
          />
        ) : (
          <TableFilterSelect
            name={fieldName}
            placeholder={`${mobilePrefix}${' '}${mobileHeaderContent}`}
            options={options}
            type={selectType}
            filterStyles={filterStyles}
            isFiltersLoading={isFiltersLoading}
            muiSelectProps={muiSelectProps}
            filterContainerStyles={filterContainerStyles}
            initialValue={filterValue}
            disabled={disabled}
          />
        )}
      </form>
    </FormProvider>
  )
}

TableFilter.propTypes = {
  column: PropTypes.shape({
    filterValue: PropTypes.string,
    preFilteredRows: PropTypes.array,
    id: PropTypes.string,
    render: PropTypes.func,
    serverSideFilterOptions: PropTypes.array,
    hasCutomFilterHeader: PropTypes.bool,
    filterStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    filterContainerStyles: PropTypes.object,
    filterComponent: PropTypes.node,
    filterComponentStyles: PropTypes.object,
  }),
  setFilter: PropTypes.func,
  filterMode: PropTypes.oneOf(Object.values(FILTER_MODES)),
  filterSelectType: PropTypes.oneOf(Object.values(FILTER_SELECT_TYPES), PropTypes.func),
  shouldClearFilters: PropTypes.bool,
  isFiltersLoading: PropTypes.bool,
  setShouldClearFilters: PropTypes.func,
  muiSelectProps: PropTypes.object,
  disabled: PropTypes.bool,
}

function areEqual(prevProps, nextProps) {
  return (
    prevProps.column.serverSideFilterOptions === nextProps.column.serverSideFilterOptions &&
    prevProps.column.filterValue === nextProps.column.filterValue &&
    prevProps.shouldClearFilters === nextProps.shouldClearFilters &&
    prevProps.disabled === nextProps.disabled
  )
}

export default memo(TableFilter, areEqual)
